import moment, { Moment } from 'moment';

import TimeFrame from 'features/residents/models/configurations/time-frame';

export interface FormattedTimeFrame {
  formattedTimeFrameText: string;
  isInTimeFrame: boolean;
}

const fullDayTimeFrame: TimeFrame = {
  from: '00:00:00.000',
  to: '23:59:59.999',
};

export const getClosestTimeFrame = (timeFrames: TimeFrame[], timeToBeClosestTo?: Moment): FormattedTimeFrame => {
  const sortedTimeFrames = [...timeFrames]
    .filter((x) => x.from != null && x.to != null)
    .sort((a, b) => toMinutes(a.from) - toMinutes(b.from));

  if (sortedTimeFrames.length === 0) {
    return { formattedTimeFrameText: '', isInTimeFrame: false };
  }

  const now = timeToBeClosestTo ?? moment();
  const nowTotalMinutes = toMinutes(now.format('HH:mm'));

  for (let i = 0; i < sortedTimeFrames.length; i++) {
    const currentTimeFrame = sortedTimeFrames[i];
    const currentFrom = toMinutes(currentTimeFrame.from);
    const currentTo = toMinutes(currentTimeFrame.to);

    if (isInTimeFrame(currentFrom, currentTo, nowTotalMinutes)) {
      return {
        formattedTimeFrameText: formatToHHmm(currentTimeFrame.to),
        isInTimeFrame: true,
      };
    }

    if (i < sortedTimeFrames.length - 1) {
      const nextFrame = sortedTimeFrames[i + 1];
      const nextFrom = toMinutes(nextFrame.from);

      if (currentTo !== nextFrom && isInTimeFrame(currentTo, nextFrom, nowTotalMinutes)) {
        return {
          formattedTimeFrameText: formatToHHmm(nextFrame.from),
          isInTimeFrame: false,
        };
      }
    }
  }

  return {
    formattedTimeFrameText: formatToHHmm(sortedTimeFrames[0].from),
    isInTimeFrame: false,
  };
};

const isInTimeFrame = (from: number, to: number, current: number): boolean => {
  if (to > from) {
    return current >= from && current < to;
  }
  return current >= from || current < to;
};

export const validateTimeFrames = (timeFrames: TimeFrame[]): boolean[] => {
  return timeFrames.map((timeFrame, index) => {
    if (isInvalidOrEmptyDate(timeFrame.from) || isInvalidOrEmptyDate(timeFrame.to)) {
      return true;
    }

    const currentStart = toMinutes(timeFrame.from);
    const currentEnd = toMinutes(timeFrame.to);

    return timeFrames.some((compareTimeFrame, compareIndex) => {
      if (index === compareIndex) return false;

      if (isInvalidOrEmptyDate(compareTimeFrame.from) || isInvalidOrEmptyDate(compareTimeFrame.to)) {
        return false;
      }

      const compareStart = toMinutes(compareTimeFrame.from);
      const compareEnd = toMinutes(compareTimeFrame.to);

      return isInTimeFrame(compareStart, compareEnd, currentStart) || isInTimeFrame(currentStart, currentEnd, compareStart);
    });
  });
};

export const validateContinuityOfTimeFrames = (timeFrames: TimeFrame[]): boolean => {
  if (!timeFrames || timeFrames.length === 0) return false;

  const MINUTES_IN_DAY = 1440;

  const sortedFrames = timeFrames
    .map((tf) => ({
      from: toMinutes(tf.from),
      to: toMinutes(tf.to),
    }))
    .sort((a, b) => a.from - b.from);

  for (let i = 0; i < sortedFrames.length; i++) {
    const currentFrame = sortedFrames[i];
    const nextFrame = sortedFrames[(i + 1) % sortedFrames.length];
    const gap = (nextFrame.from - currentFrame.to + MINUTES_IN_DAY) % MINUTES_IN_DAY;

    if (gap !== 0) {
      return false;
    }
  }

  return true;
};

const toMinutes = (time: string | null): number => {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const isInvalidOrEmptyDate = (date: string | null): boolean => {
  return !date || date === 'Invalid date' || date === '';
};

export const hasAllDayTimeFrame = (timeFrames: TimeFrame[] | null): boolean => {
  if (!timeFrames || timeFrames.length < 1 || timeFrames.length > 1) {
    return false;
  }

  return timeFrames.some((timeFrame) => timeFrame.from === fullDayTimeFrame.from && timeFrame.to === fullDayTimeFrame.to);
};

const formatToHHmm = (time: string | null | undefined): string => {
  if (!time) return '';
  return moment(time, ['HH:mm', 'HH:mm:ss', 'HH:mm:ss.SSS']).format('HH:mm');
};
